/**
 * Helper functions for tracking model run events with comprehensive metrics
 */
import { useModelStore } from "@demo/store/model";
import useSessionStore from "@demo/store/session";

import { analytics } from ".";
import { ANALYTICS_EVENTS, AnalyticsEventProperties } from "./types";

type ExcludedParams = "model" | "timestamp" | "sessionId";

export const trackModelRun = {
  start: (params: Omit<AnalyticsEventProperties[typeof ANALYTICS_EVENTS.MODEL_RUN_STARTED], ExcludedParams>) => {
    const sessionId = useSessionStore.getState().sessionUuid;
    const model = useModelStore.getState().selectedModel;
    analytics.track(ANALYTICS_EVENTS.MODEL_RUN_STARTED, {
      ...params,
      model: model.name,
      sessionId: sessionId,
      timestamp: Date.now(),
    });
  },

  complete: (params: Omit<AnalyticsEventProperties[typeof ANALYTICS_EVENTS.MODEL_RUN_COMPLETED], ExcludedParams>) => {
    const sessionId = useSessionStore.getState().sessionUuid;
    const model = useModelStore.getState().selectedModel;
    analytics.track(ANALYTICS_EVENTS.MODEL_RUN_COMPLETED, {
      ...params,
      model: model.name,
      sessionId: sessionId,
      timestamp: Date.now(),
    });
  },

  fail: (params: Omit<AnalyticsEventProperties[typeof ANALYTICS_EVENTS.MODEL_RUN_FAILED], ExcludedParams>) => {
    const sessionId = useSessionStore.getState().sessionUuid;
    const model = useModelStore.getState().selectedModel;
    analytics.track(ANALYTICS_EVENTS.MODEL_RUN_FAILED, {
      ...params,
      model: model.name,
      sessionId: sessionId,
      timestamp: Date.now(),
    });
  },

  cancel: (params: Omit<AnalyticsEventProperties[typeof ANALYTICS_EVENTS.MODEL_RUN_CANCELLED], ExcludedParams>) => {
    const sessionId = useSessionStore.getState().sessionUuid;
    const model = useModelStore.getState().selectedModel;
    analytics.track(ANALYTICS_EVENTS.MODEL_RUN_CANCELLED, {
      ...params,
      model: model.name,
      sessionId: sessionId,
      timestamp: Date.now(),
    });
  },
};

/** The gateway route detect runs target through the demo proxy (`DetectCommonFields.route` parity). */
const DETECT_ROUTE = "/v1/detect";

type ExcludedDetectParams = "route" | "session_id" | "timestamp";

/**
 * Helper functions for the detect-mode events. Mirrors {@link trackModelRun},
 * auto-filling the common fields (`route`, `session_id`, `timestamp`) in the
 * gateway's snake_case naming. Unlike `trackModelRun` there is no `model`
 * stamp — the detect proxy never specifies one (see the detect-events note in
 * `./types`).
 */
export const trackDetect = {
  start: (params: Omit<AnalyticsEventProperties[typeof ANALYTICS_EVENTS.DETECT_STARTED], ExcludedDetectParams>) => {
    analytics.track(ANALYTICS_EVENTS.DETECT_STARTED, {
      ...params,
      route: DETECT_ROUTE,
      session_id: useSessionStore.getState().sessionUuid,
      timestamp: Date.now(),
    });
  },

  complete: (
    params: Omit<AnalyticsEventProperties[typeof ANALYTICS_EVENTS.DETECT_COMPLETED], ExcludedDetectParams>,
  ) => {
    analytics.track(ANALYTICS_EVENTS.DETECT_COMPLETED, {
      ...params,
      route: DETECT_ROUTE,
      session_id: useSessionStore.getState().sessionUuid,
      timestamp: Date.now(),
    });
  },

  fail: (params: Omit<AnalyticsEventProperties[typeof ANALYTICS_EVENTS.DETECT_FAILED], ExcludedDetectParams>) => {
    analytics.track(ANALYTICS_EVENTS.DETECT_FAILED, {
      ...params,
      route: DETECT_ROUTE,
      session_id: useSessionStore.getState().sessionUuid,
      timestamp: Date.now(),
    });
  },

  cancel: (params: Omit<AnalyticsEventProperties[typeof ANALYTICS_EVENTS.DETECT_CANCELLED], ExcludedDetectParams>) => {
    analytics.track(ANALYTICS_EVENTS.DETECT_CANCELLED, {
      ...params,
      route: DETECT_ROUTE,
      session_id: useSessionStore.getState().sessionUuid,
      timestamp: Date.now(),
    });
  },
};

/**
 * Helper functions for tracking media-related events
 */
export const trackMedia = {
  upload: (mediaType: "image" | "video", fileSize: number, fileName: string) => {
    analytics.track(ANALYTICS_EVENTS.MEDIA_UPLOADED, {
      mediaType,
      fileSize,
      fileName,
    });
  },

  delete: (mediaId: string, mediaType: "image" | "video") => {
    analytics.track(ANALYTICS_EVENTS.MEDIA_DELETED, {
      mediaId,
      mediaType,
    });
  },
};
